import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuCircleCheckBig, LuPackageCheck, LuThumbsUp, LuUndo2 } from "react-icons/lu";
import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import TextInput from "../../../common/TextInput";
import { notify } from "../../../common/Toast";
import { approveRequest, sendOrder, receiveRequest } from "../../../redux/action/requestsAction/RequestsAction";
import { LOCATIONS } from "../../../utils/Constant";



export default function RequestStatusSelect({ requestId, status, onChanged }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { statusLoading } = useSelector((state) => state.request);

  const [receivedOpen, setReceivedOpen] = useState(false);
  const [expireDate, setExpireDate] = useState("");
  const [location, setLocation] = useState("");
  const [customLocation, setCustomLocation] = useState("");

  const handleApprove = async (undo = false) => {
    const res = await dispatch(approveRequest(requestId, user?.email, undo));
    if (res.success) {
      notify.success(undo ? "Request Approval Withdrawn" : "Request Approved");
      onChanged?.();
    } else {
      notify.error(undo ? "Failed to undo approve request" : "Failed to approve request", res.error);
    }
  };

  const handleSendOrder = async (undo = false) => {
    const res = await dispatch(sendOrder(requestId, user?.email, undo));
    if (res.success) {
      notify.success(undo ? "Order Has Not Been Placed" : "Order Placed");
      onChanged?.();
    } else {
      notify.error("Failed to update request status", res.error);
    }
  };

  const handleReceive = async () => {
    const finalLocation = location === "Custom" ? customLocation : location;
    const res = await dispatch(receiveRequest({ id: requestId, expireDate, location: finalLocation }));
    if (res.success) {
      notify.success("Item Received", "Item has been successfully received and logged in inventory.");
      setReceivedOpen(false);
      setExpireDate("");
      setLocation("");
      setCustomLocation("");
      onChanged?.();
    } else {
      notify.error("Failed to set request status to received", res.error);
    }
  };

  const btnBase =
    "inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60";

  if (status === "new") {
    return user?.admin ? (
      <button
        onClick={() => handleApprove(false)}
        disabled={statusLoading}
        className={`${btnBase} bg-teal-600 text-white hover:bg-teal-700`}
      >
        <LuThumbsUp size={14} /> Approve
      </button>
    ) : (
      <span className={`${btnBase} cursor-default bg-slate-100 text-slate-400`}>
        Awaiting Approval…
      </span>
    );
  }

  if (status === "approved") {
    return (
      <div className="flex w-full items-center gap-1.5">
        <span className={`${btnBase} cursor-default bg-slate-100 text-slate-400`}>
          Awaiting PO…
        </span>
        {user?.admin && (
          <button
            onClick={() => handleApprove(true)}
            disabled={statusLoading}
            title="Undo approval"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-teal-200 text-teal-600 hover:bg-teal-50"
          >
            <LuUndo2 size={14} />
          </button>
        )}
      </div>
    );
  }

  if (status === "poMade") {
    return (
      <button
        onClick={() => handleSendOrder(false)}
        disabled={statusLoading}
        className={`${btnBase} bg-teal-600 text-white hover:bg-teal-700`}
      >
        <LuCircleCheckBig size={14} /> Ordered
      </button>
    );
  }

  if (status === "ordered") {
    return (
      <>
        <div className="flex w-full items-center gap-1.5">
          <button
            onClick={() => setReceivedOpen(true)}
            className={`${btnBase} bg-teal-600 text-white hover:bg-teal-700`}
          >
            <LuPackageCheck size={14} /> Received
          </button>
          <button
            onClick={() => handleSendOrder(true)}
            disabled={statusLoading}
            title="Undo order"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-teal-200 text-teal-600 hover:bg-teal-50"
          >
            <LuUndo2 size={14} />
          </button>
        </div>

        <Modal open={receivedOpen} onClose={() => setReceivedOpen(false)} title="Add Item to Inventory">
          <div className="space-y-3">
            <TextInput
              label="Expiration Date"
              required
              type="date"
              value={expireDate}
              onChange={(e) => setExpireDate(e.target.value)}
            />
            <div>
              <label className="mb-1 block text-[12px] font-medium text-slate-600">
                Location<span className="ml-0.5 text-red-500">*</span>
              </label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              >
                <option value="">Select location</option>
                {LOCATIONS.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
                <option value="Custom">Custom</option>
              </select>
            </div>
            {location === "Custom" && (
              <TextInput
                label="Custom Location"
                required
                value={customLocation}
                onChange={(e) => setCustomLocation(e.target.value)}
                placeholder="Enter custom location"
              />
            )}
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReceivedOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleReceive}
              disabled={!expireDate || (!location || (location === "Custom" && !customLocation.trim()))}
              loading={statusLoading}
            >
              Done
            </Button>
          </div>
        </Modal>
      </>
    );
  }

  return null;
}